/** Admin Media — coverage tracking (social + traditional). Mock-first; not CMS assets. */

export type MediaChannel = 'social' | 'traditional';

export type MediaKind = 'post' | 'mention' | 'publication' | 'clip';

export type MediaClass = 'owned' | 'earned' | 'shared' | 'paid';

export type MediaSentiment = 'positive' | 'neutral' | 'negative' | 'unknown';

export type MediaItemStatus = 'new' | 'reviewed' | 'flagged' | 'archived';

export type MediaSubject = 'brand' | 'competitor' | 'topic';

export type MediaIngestSource = 'manual' | 'url' | 'rss' | 'webhook' | 'vendor' | 'discover';

export type MeasurementBasis = 'public' | 'connected';

export type { SocialPlatform, TraditionalMedium } from '../marketing/platforms';
export {
  PLATFORM_LABEL, PLATFORM_ACCENT, PLATFORM_CHART_COLOR, PLATFORM_CHAR_LIMIT, MEDIUM_LABEL,
  PUBLISHABLE_PLATFORMS, ALL_SOCIAL_PLATFORMS, isPublishable, normalizeSocialPlatform,
} from '../marketing/platforms';
import type { SocialPlatform, TraditionalMedium } from '../marketing/platforms';


export type SourceTier = 1 | 2 | 3;

export type EntityLinkType = 'org' | 'listing' | 'user' | 'campaign' | 'cms';

export interface MediaEntityLink {
  type: EntityLinkType;
  id: string;
  label: string;
}

export interface MediaMetrics {
  reach?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  views?: number;
  ave?: number;
  prValue?: number;
  /** Social-only extras */
  impressions?: number;
  saves?: number;
  replies?: number;
  engagementRate?: number;
}

export type SocialPostFormat =
  | 'text'
  | 'image'
  | 'carousel'
  | 'video'
  | 'reel'
  | 'short'
  | 'thread'
  | 'story';

/** Platform-native details for social previews & tracking. */
export interface SocialPostDetails {
  format: SocialPostFormat;
  /** Links to SourceAccount.id for account-level filtering */
  accountId?: string;
  authorHandle: string;
  authorDisplayName: string;
  authorVerified?: boolean;
  authorFollowers?: number;
  caption?: string;
  hashtags?: string[];
  mentions?: string[];
  /** Short label shown on media chrome, e.g. "0:47" or "3 photos" */
  mediaLabel?: string;
  /** Tailwind gradient classes for mock media surface */
  mediaTone?: string;
  isReply?: boolean;
  isRepost?: boolean;
  quotedText?: string;
  location?: string;
  /** Tracking: first-seen vs last engagement sample */
  lastEngagementAt?: string;
  trackedSince?: string;
}

export interface CoverageItem {
  id: string;
  channel: MediaChannel;
  kind: MediaKind;
  mediaClass: MediaClass;
  platform?: SocialPlatform;
  outletId?: string;
  outletName?: string;
  title: string;
  summary: string;
  bodyExcerpt?: string;
  url?: string;
  publishedAt: string;
  capturedAt: string;
  author?: string;
  language: string;
  sentiment: MediaSentiment;
  sentimentConfidence?: number;
  status: MediaItemStatus;
  /**
   * Null when the server never scored urgency (e.g. discover). Do not coerce to
   * 0 — a zero drives crisis tiles as if we measured calm.
   */
  urgencyScore: number | null;
  assigneeName?: string;
  internalNote?: string;
  tags: string[];
  topics: string[];
  matchedRuleIds: string[];
  links: MediaEntityLink[];
  /** Richest metrics (Zernio on owned publish rows). */
  metrics: MediaMetrics;
  /**
   * Uniform public-surface metrics for fair comparison. Prefer this for SoV /
   * Tracked Performance. Absent keys mean the platform did not report them.
   */
  metricsPublic?: MediaMetrics;
  /** Discover rows are always `"public"`. */
  measurementBasis?: MeasurementBasis;
  subject: MediaSubject;
  sourceIngest: MediaIngestSource;
  /** Present when channel === 'social' */
  social?: SocialPostDetails;
}

/**
 * Prefer `metricsPublic` for any chart that compares accounts (SoV, Tracked
 * Performance). Prefer `metrics` only on Owned Performance for connected depth.
 */
export function publicMetrics(item: Pick<CoverageItem, 'metrics' | 'metricsPublic'>): MediaMetrics {
  return item.metricsPublic ?? item.metrics;
}

export interface SourceAccount {
  id: string;
  platform: SocialPlatform;
  handle: string;
  displayName: string;
  owned: boolean;
  /** Ship Inflows content to this account. */
  useAsDestination: boolean;
  /** Include in Outflows listening / coverage filters. */
  useAsListen: boolean;
  active: boolean;
  tier: SourceTier;
  followers?: number;
  verified?: boolean;
  region?: string;
  topics: string[];
  ownerName?: string;
  contactEmail?: string;
  profileUrl?: string;
  lastCoveredAt?: string;
  notes?: string;
  /** Partner influencer vs brand owned vs competitor listen target. */
  ownership?: 'owned' | 'partner_influencer' | 'competitor' | 'other';
}

export interface MediaOutlet {
  id: string;
  name: string;
  medium: TraditionalMedium;
  tier: SourceTier;
  /** Pitch / place Inflows traditional content here. */
  useAsDestination: boolean;
  /** Include in Outflows traditional coverage filters. */
  useAsListen: boolean;
  active: boolean;
  region?: string;
  website?: string;
  beats: string[];
  ownerName?: string;
  contactEmail?: string;
  pitchEmail?: string;
  reach?: number;
  doNotPitch?: boolean;
  lastCoveredAt?: string;
  notes?: string;
  ownership?: 'owned' | 'partner_media' | 'news_outlet';
}

export interface ListeningRule {
  id: string;
  name: string;
  queryInclude: string;
  queryExclude?: string;
  channels: MediaChannel[];
  enabled: boolean;
  alertOnMatch: boolean;
  alertVolumeSpike: boolean;
  alertSentimentDrop: boolean;
  matchCount: number;
  lastMatchedAt?: string;
}

export type BriefStatus = 'draft' | 'published';

export interface MediaBrief {
  id: string;
  title: string;
  periodLabel: string;
  status: BriefStatus;
  itemIds: string[];
  createdAt: string;
  updatedAt: string;
}

/** Mentions / coverage keyword tracker (single term or combination). */
export type KeywordMatchMode = 'any' | 'all';

export interface TrackedKeyword {
  id: string;
  /** Short label shown in filters */
  label: string;
  /**
   * Terms to match. Hashtags may include or omit leading `#`
   * (normalized at match time). Plain words match caption/title/body.
   */
  terms: string[];
  /** `all` = AND combination; `any` = OR */
  matchMode: KeywordMatchMode;
  /** Which channels this tracker applies to */
  channels: MediaChannel[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const MEDIA_STATUS_BADGE: Record<MediaItemStatus, string> = {
  new: 'bg-blue-50 text-blue-700',
  reviewed: 'bg-emerald-50 text-emerald-700',
  flagged: 'bg-red-50 text-red-700',
  archived: 'bg-gray-100 text-gray-600',
};

export const MEDIA_SENTIMENT_BADGE: Record<MediaSentiment, string> = {
  positive: 'bg-emerald-50 text-emerald-700',
  neutral: 'bg-gray-100 text-gray-700',
  negative: 'bg-red-50 text-red-700',
  unknown: 'bg-amber-50 text-amber-800',
};

export const MEDIA_CLASS_BADGE: Record<MediaClass, string> = {
  owned: 'bg-violet-50 text-violet-700',
  earned: 'bg-sky-50 text-sky-700',
  shared: 'bg-teal-50 text-teal-700',
  paid: 'bg-orange-50 text-orange-700',
};

export const SOCIAL_FORMAT_LABEL: Record<SocialPostFormat, string> = {
  text: 'Text',
  image: 'Image',
  carousel: 'Carousel',
  video: 'Video',
  reel: 'Reel',
  short: 'Short',
  thread: 'Thread',
  story: 'Story',
};

